﻿import { RundotGameAPI } from '../rundot-game-api/types'
import { Host } from '../Host'
import { RoomsApi } from './RoomsApi'

export * from './RoomsApi'
export * from './RundotGameRoom'
export * from './RpcRoomsApi'
export * from './setupRoomNotifications'
// HttpRoomsApi, FirestoreRoomsApi, CompositeRemoteRoomsApi are intentionally NOT exported here.
// They depend on Firebase (via callRemoteFunction) and should only be imported by PlaygroundHost.
// This prevents Firebase from being bundled in production builds.

type MutableRundotGameApi = RundotGameAPI & Record<string, any>
type MutableRoomsApi = RoomsApi & Record<string, any>

const ROOMS_DEPRECATION_REPLACEMENTS: Record<string, string> = {
  createRoomAsync: 'RundotGameAPI.realtime.createRoom(roomType)',
  joinOrCreateRoomAsync: 'RundotGameAPI.realtime.joinOrCreateRoom(roomType)',
  joinRoomByCodeAsync: 'RundotGameAPI.realtime.joinRoomByCode(code)',
  getUserRoomsAsync: 'RundotGameAPI.realtime.getUserRooms(options?)',
  subscribeAsync: 'room.on() on a RundotGameAPI.realtime room',
  sendRoomMessageAsync: 'room.send() on a RundotGameAPI.realtime room',
  leaveRoomAsync: 'room.leave() on a RundotGameAPI.realtime room',
  updateRoomDataAsync: 'server-authoritative state in your realtime GameRoom class',
  getRoomDataAsync: 'server-authoritative state in your realtime GameRoom class',
  kickPlayerAsync: 'this.kick() in your realtime GameRoom class',
  startRoomGameAsync: 'lifecycle logic in your realtime GameRoom class',
  proposeMoveAsync: 'onGameMessage() handling in your realtime GameRoom class',
  validateMoveAsync: 'onGameMessage() handling in your realtime GameRoom class',
}

function bindMethod(
  target: Record<string, any>,
  targetKey: string,
  source: MutableRoomsApi,
  warn: (method: string) => void,
  sourceKey?: string,
) {
  const key = sourceKey ?? targetKey
  const fn = source?.[key]
  if (typeof fn === 'function') {
    target[targetKey] = (...args: unknown[]) => {
      warn(targetKey)
      return (fn as (...a: unknown[]) => unknown).apply(source, args)
    }
    return true
  }
  return false
}

export function initializeRoomsApi(rundotGameApi: RundotGameAPI, host: Host) {
  const roomsApi = host?.rooms as MutableRoomsApi | undefined
  if (!roomsApi) {
    console.warn(
      '[RUN] Host did not provide a rooms implementation. Rooms API will be unavailable.',
    )
    return
  }

  const warned = new Set<string>()
  const warnDeprecated = (method: string): void => {
    if (warned.has(method)) return
    warned.add(method)
    const replacement = ROOMS_DEPRECATION_REPLACEMENTS[method] ?? 'RundotGameAPI.realtime'
    console.warn(
      `[RUN] DEPRECATED: RundotGameAPI.rooms.${method}() is deprecated and unsupported. ` +
        `Realtime (RundotGameAPI.realtime) is the only supported multiplayer API — use ${replacement}. ` +
        `See the Multiplayer docs for the migration guide.`,
    )
  }

  const rundotGame = rundotGameApi as MutableRundotGameApi
  const existingNamespace = (rundotGame.rooms || {}) as Record<string, any>
  const roomsNamespace: Record<string, any> = Object.assign({}, existingNamespace)

  const namespaceBindings: Array<[string, string?]> = [
    ['createRoomAsync'],
    ['joinOrCreateRoomAsync'],
    ['joinRoomByCodeAsync'],
    ['getUserRoomsAsync'],
    ['subscribeAsync'],
    ['updateRoomDataAsync'],
    ['getRoomDataAsync'],
    ['sendRoomMessageAsync'],
    ['leaveRoomAsync'],
    ['kickPlayerAsync'],
    ['startRoomGameAsync'],
    ['proposeMoveAsync'],
    ['validateMoveAsync'],
  ]

  namespaceBindings.forEach(([targetKey, sourceKey]) => {
    bindMethod(roomsNamespace, targetKey, roomsApi, warnDeprecated, sourceKey)
  })

  rundotGame.rooms = roomsNamespace as any
}
